import _ from "lodash";
import React, { useState, useMemo, useLayoutEffect } from "react";
import { useI18n } from "Hooks";

import { Tooltip, Button, IconButton, Alert } from "@toddle-edu/ds-web";
import { ReloadArrowOutlined } from "@toddle-edu/ds-icons";

import SkeletonLoader from "UIComponents/SkeletonLoader";
import { updateAiMessage } from "../../../../modules/AIChatModules";
import { getAIResponse } from "modules/Services";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { useDispatch } from "Hooks/useDispatch";
import {
  getAIConversationsFromCache,
  writeAIConversationsInCache,
} from "Announcements/components/AnnouncementAssistant/modules/AnnouncementAssistantGraphqlHelpers";
import { getSelectedLearningCourseId } from "LearningCourse/modules/Utils";
import {
  getClassCourseIds,
  ENTITY_TYPE,
  CREATED_IN_OPTIONS,
} from "Announcements/modules/utils";
import { connect } from "react-redux";
import classes from "./BannerImageBlock.scss";
import { REGEX_PATTERNS } from "../../modules/Constants";
import { onEnterOrSpaceKeyDown } from "Utils";
import { getSelectedCurriculumId } from "modules/CommonSelectors";

const BannerImageBlock = props => {
  const {
    node,
    url,
    conversation,
    announcementId,
    isInClassAnnouncement,
    selectedCourseId,
    learningCourseId,
    curriculumProgramId,
    loadingBanner,
    onImageClick,
  } = props;

  const courseIds = useMemo(() => {
    return getClassCourseIds({
      selectedCourseId,
      learningCourseId,
      entityType: ENTITY_TYPE.USER,
    });
  }, [selectedCourseId, learningCourseId]);

  const [hasImageError, setHasImageError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const { t } = useI18n();
  const dispatch = useDispatch();
  const { id: nodeId } = node || {};

  useLayoutEffect(() => {
    /* when internal retry is triggered, the isImageLoaded flag would be true always, resulting in old image being rendered for a fraction of second.
     hence setting this to false every time the url changes
    */
    setIsImageLoaded(false);
  }, [url]);

  const isLastMessageWithBanner = useMemo(() => {
    const messageNode = _.get(conversation, "messages.edges[0].node");

    return (
      _.get(messageNode, "authorType") === "assistant" &&
      REGEX_PATTERNS.BANNER_IMAGE_TAG_PATTERN.test(
        _.get(messageNode, "content", "")
      ) &&
      _.get(messageNode, "id") === _.get(node, "id")
    );
  }, [conversation, node]);

  const handleRetry = async e => {
    e?.stopPropagation();
    if (!node) return;

    setLoading(true);
    const messages = _.get(conversation, "messages.edges", []);
    const aiConversationId = _.get(conversation, "id");
    const originalContent = _.get(node, "content", "");
    const nodeId = _.get(node, "id", "");
    const messageId = _.get(
      _.find(messages, ({ node }) => {
        const authorType = _.get(node, "authorType");
        const content = _.get(node, "content", "");
        const toolCalls = _.get(node, "metadata.toolCalls", []);

        return (
          authorType === "assistant" &&
          _.isEmpty(content) &&
          _.some(
            toolCalls,
            toolCall => _.get(toolCall, "function.name") === "create_banner"
          )
        );
      }),
      "node.id",
      ""
    );

    try {
      const createBannerInput = {
        context: {
          inputText: "Please create the banner, the previous one didn't load",
        },
        entityId: announcementId,
        entityType: "ANNOUNCEMENT_AI",
        curriculumProgramId: curriculumProgramId,
        shouldProcessAttachments: true,
        metadata: {
          conversationId: aiConversationId,
          announcementId,
          createdIn: isInClassAnnouncement
            ? CREATED_IN_OPTIONS.CLASS
            : CREATED_IN_OPTIONS.GLOBAL,
          courseIds: isInClassAnnouncement ? courseIds : [],
          announcementClassId: isInClassAnnouncement ? selectedCourseId : null,
        },
      };

      const regenerateBannerInput = {
        action: "CREATE_BANNER",
        entityId: announcementId,
        entityType: "ANNOUNCEMENT_AI",
        shouldProcessAttachments: true,
        metadata: {
          conversationId: aiConversationId,
          announcementId,
          regenerateBannerMessageId: messageId,
          createdIn: isInClassAnnouncement
            ? CREATED_IN_OPTIONS.CLASS
            : CREATED_IN_OPTIONS.GLOBAL,
          courseIds: isInClassAnnouncement ? courseIds : [],
          announcementClassId: isInClassAnnouncement ? selectedCourseId : null,
        },
      };

      const response = await dispatch(
        getAIResponse({
          useStreamParserV2: true,
          variables: {
            input: _.isEmpty(messageId)
              ? createBannerInput
              : regenerateBannerInput,
          },
        })
      );

      const responseContent = _.get(response, "content", "");

      const finalBannerUrl = (_.isEmpty(messageId)
        ? responseContent.match(
            /<banner_image>([\s\S]*?)<\/banner_image>/i
          )?.[1]
        : responseContent.match(/!\[.*?\]\((.*?)\)/)?.[1]
      )?.trim();

      if (!_.isEmpty(finalBannerUrl)) {
        const updatedContent = originalContent.replace(
          /<banner_image>[\s\S]*?<\/banner_image>/i,
          `<banner_image>${finalBannerUrl}</banner_image>`
        );

        const cachedConversation = getAIConversationsFromCache({
          id: aiConversationId,
        });

        const updatedEdges = _.map(
          _.get(cachedConversation, "messages.edges", []),
          edge => {
            if (_.get(edge, "node.id") !== nodeId) return edge;

            return {
              ...edge,
              node: { ...edge.node, content: updatedContent },
            };
          }
        );

        writeAIConversationsInCache({
          variables: { id: aiConversationId },
          data: {
            node: {
              ...cachedConversation,
              messages: {
                ...cachedConversation.messages,
                edges: updatedEdges,
              },
            },
          },
        });

        await dispatch(
          updateAiMessage({
            id: nodeId,
            content: updatedContent,
          })
        );
        setHasImageError(false);
      }
    } catch {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    } finally {
      setLoading(false);
    }
  };

  const handleImageError = () => {
    setHasImageError(true);
  };

  const isLoading = loading || loadingBanner;

  if (!isLoading && (hasImageError || _.isEmpty(url))) {
    return (
      <Alert
        dsVersion="2.0"
        type="warning"
        showIcon
        title={t("ai:something_went_wrong")}
        message={t("ai:banner_image_load_error")}
        actionElement={
          <Button
            dsVersion="2.0"
            type="outlined"
            variant="neutral"
            size="medium"
            onClick={handleRetry}
            disabled={loading}
          >
            {t("common:retry")}
          </Button>
        }
      />
    );
  }

  const handleBannerClick = () => {
    if (isLoading || !isImageLoaded || _.isEmpty(url) || !onImageClick) {
      return;
    }

    onImageClick({
      attachments: [
        { url, type: "IMAGE", name: "banner", mimeType: "image/*" },
      ],
      activeAttachmentIndex: 0,
    });
  };

  return (
    <div
      className={classes.bannerImageContainer}
      role="button"
      id={`${nodeId}_AI_MESSAGE`}
      tabIndex={0}
      aria-label="View image"
      onClick={handleBannerClick}
      onKeyDown={e => onEnterOrSpaceKeyDown(e, handleBannerClick)}
    >
      {(isLoading || !isImageLoaded) && (
        <SkeletonLoader
          width={"100%"}
          height="auto"
          customClasses={classes.skeletonLoader}
        />
      )}
      <img
        src={url}
        alt="banner"
        onError={handleImageError}
        onLoad={() => setIsImageLoaded(true)}
        className={!isImageLoaded ? classes.hiddenImage : undefined}
      />
      {isLastMessageWithBanner && isImageLoaded ? (
        <div className={classes.bannerOverlay}>
          <Tooltip
            tooltip={t("ai:regenerate_banner")}
            placement="bottom"
            dsVersion="2.0"
          >
            <IconButton
              icon={<ReloadArrowOutlined />}
              variant="neutral"
              type="outlined"
              size="x-small"
              dsVersion="2.0"
              onClick={handleRetry}
              disabled={loading}
              className={classes.bannerRegenerateButton}
            />
          </Tooltip>
        </div>
      ) : null}
    </div>
  );
};

const mapStateToProps = state => {
  const selectedCourseId = _.get(
    state,
    "teacher.selected_class.selected_course",
    ""
  );
  const curriculumProgramId = getSelectedCurriculumId(state);
  const isInClassAnnouncement = !_.isEmpty(selectedCourseId);
  const learningCourseId = getSelectedLearningCourseId(state) || "";

  return {
    isInClassAnnouncement,
    selectedCourseId,
    learningCourseId,
    curriculumProgramId,
  };
};

export default connect(mapStateToProps)(BannerImageBlock);
